import { config } from "@config/config";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { Button, Dialog, Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportEhrSyncChoices,
  ApiReportReportStatusChoices,
  ApiReportTemplateExportFrameworkChoices,
  ExportTypeEnum,
  ReportTypeEnums,
  useCreateExportReportGenerationMutation,
  useReportStatusQuery,
} from "@api/graphql/types-and-hooks";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { useInFlightOnce } from "@hooks/useInFlightOnce";
import { ReportNode } from "@providers/ReportProvider";

export type ExportOption = {
  id: ExportTypeEnum;
  primary: string;
};

export const EXPORT_OPTIONS: ExportOption[] = [
  {
    id: ExportTypeEnum.Docx,
    primary: "Microsoft Word (.docx) BETA",
  },
  {
    id: ExportTypeEnum.Pdf,
    primary: "PDF Document (.pdf)",
  },
];

interface ReportExportDialogProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onExport: () => void | Promise<void>;
  onRetrySuccess: () => void | Promise<void>;
  selectedExportType: SelectItem;
  setSelectedExportType: (item: SelectItem) => void;
  isExporting?: boolean;
  report: ReportNode;
  isReportLoading: boolean;
}

export const ReportExportDialog: React.FC<ReportExportDialogProps> = ({
  open,
  setOpen,
  onExport,
  onRetrySuccess,
  selectedExportType,
  setSelectedExportType,
  isExporting = false,
  report,
  isReportLoading,
}) => {
  const hasDocxUrl = !!report?.reportDocxFileSignedUrl;
  const ehrSyncCompleted = report?.ehrSync === ApiReportEhrSyncChoices.Completed;
  const generateTypescriptExportReport = useCreateExportReportGenerationMutation({});
  const isUnifiedExport = report?.reportTemplate?.unifiedExport ?? false;
  const [exportError, setExportError] = useState<string | null>(null);
  const [shouldPoll, setShouldPoll] = useState(false);
  const hasHandledCompletionRef = useRef(false);
  const pollAttemptsRef = useRef(0);
  const isInitialOpenRef = useRef(false);
  const MAX_POLL_ATTEMPTS = 60;

  const { data: reportStatusData } = useReportStatusQuery(
    { reportId: report.id },
    {
      queryKey: ["report-status", report.id],
      enabled: shouldPoll,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: true,
      refetchInterval: (query) => {
        const status = query.state?.data?.getReport?.edges?.[0]?.node?.reportStatus;

        if (status === ApiReportReportStatusChoices.Generating) {
          pollAttemptsRef.current += 1;
          if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
            return false;
          }
          return config.REPORT_POLLING_INTERVAL || 3000;
        }

        return false;
      },
    },
  );

  useEffect(() => {
    if (reportStatusData && shouldPoll) {
      const status = reportStatusData?.getReport?.edges?.[0]?.node?.reportStatus;

      if (status === ApiReportReportStatusChoices.Generating && pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
        setExportError("Report generation timed out.");
        setShouldPoll(false);
        hasHandledCompletionRef.current = true;
      }

      if (status === ApiReportReportStatusChoices.Failure) {
        setExportError("Report generation failed.");
        setShouldPoll(false);
        hasHandledCompletionRef.current = true;
      }

      if (status === ApiReportReportStatusChoices.Completed && !hasHandledCompletionRef.current) {
        hasHandledCompletionRef.current = true;
        setExportError(null);
        setShouldPoll(false);
        onRetrySuccess();
      }
    }
  }, [reportStatusData, shouldPoll, onRetrySuccess]);

  const filteredExportOptions = useMemo(() => {
    return EXPORT_OPTIONS.filter((option) => {
      if (!hasDocxUrl && ehrSyncCompleted) {
        return option.id !== ExportTypeEnum.Docx;
      }
      return true;
    });
  }, [hasDocxUrl, ehrSyncCompleted]);

  const isHandlebarsExport =
    report?.reportTemplate?.exportFramework === ApiReportTemplateExportFrameworkChoices.Handlebars ||
    (isUnifiedExport &&
      report?.reportTemplate?.exportFramework === ApiReportTemplateExportFrameworkChoices.Docxtemplater);

  useEffect(() => {
    if (open && filteredExportOptions.length > 0) {
      // Only auto-select on initial dialog open
      if (!isInitialOpenRef.current) {
        isInitialOpenRef.current = true;
        // Default to PDF for Handlebars (typescript) export framework
        if (isHandlebarsExport) {
          const pdfOption = filteredExportOptions.find((option) => option.id === ExportTypeEnum.Pdf);
          setSelectedExportType(pdfOption || filteredExportOptions[0]);
        } else {
          setSelectedExportType(filteredExportOptions[0]);
        }
      } else {
        // If current selection is no longer available, select first option
        const currentSelectionAvailable = filteredExportOptions.some((option) => option.id === selectedExportType.id);
        if (!currentSelectionAvailable) {
          setSelectedExportType(filteredExportOptions[0]);
        }
      }
    }
  }, [open, filteredExportOptions, setSelectedExportType, selectedExportType.id, isHandlebarsExport]);

  useEffect(() => {
    if (open) {
      setExportError(null);
    }
    if (!open) {
      setShouldPoll(false);
      pollAttemptsRef.current = 0;
      hasHandledCompletionRef.current = false;
      isInitialOpenRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    setExportError(null);
  }, [selectedExportType.id]);

  const isMissingSelectedFile =
    isHandlebarsExport &&
    ((selectedExportType.id === ExportTypeEnum.Docx && !report.reportDocxFileSignedUrl) ||
      (selectedExportType.id === ExportTypeEnum.Pdf && !report.reportPdfFileSignedUrl));

  const isExportErrorRelevant = exportError !== null;

  const isExportDisabled =
    (isReportLoading && selectedExportType.id === ExportTypeEnum.Docx) ||
    isMissingSelectedFile ||
    isExportErrorRelevant;

  const runRetryExport = async () => {
    setExportError(null);
    pollAttemptsRef.current = 0;
    hasHandledCompletionRef.current = false;

    try {
      await generateTypescriptExportReport.mutateAsync(
        {
          input: {
            reportId: report.id,
            learnerId: report.learner?.id || "",
            reportType: report.reportType as unknown as ReportTypeEnums,
            docxExport: selectedExportType.id === ExportTypeEnum.Docx,
          },
        },
        {
          onSuccess: () => {
            setShouldPoll(true);
          },
        },
      );
    } catch (error) {
      console.error("Error generating typescript export:", error);
      setExportError("Failed to export report");
      setShouldPoll(false);
    }
  };

  // PROA-2622: `isRetryingLoading` only pulls the button after a re-render, so
  // a fast second click still started a rival export workflow. `shouldPoll`
  // takes over once the mutation resolves.
  const handleRetryExport = useInFlightOnce(runRetryExport);

  const isRetryingLoading = shouldPoll || generateTypescriptExportReport.isPending || isReportLoading;

  return (
    <Dialog
      title="Export Report"
      open={open}
      setOpen={() => {}}
      showCloseButton={false}
      primaryButton={{
        text: (
          <div className="flex gap-1 w-max items-center">
            {isExporting && <Loader className="w-4 h-4" />}
            Export
          </div>
        ),
        onClick: () => {
          onExport();
        },
        disabled: isExportDisabled,
      }}
      secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
      size="xl"
    >
      <div className="flex flex-col gap-2">
        <Select
          title="Select a file type"
          items={filteredExportOptions}
          placeholderText="Select export format"
          selected={selectedExportType}
          setSelected={setSelectedExportType}
          enableSearch
          formNoValidate={true}
        />
        {(isExportErrorRelevant || isMissingSelectedFile || isRetryingLoading) && (
          <div className="flex items-center w-full justify-between">
            {!isRetryingLoading && (isExportErrorRelevant || isMissingSelectedFile) && (
              <Small className="text-red-500">{exportError || "The document didn't generate successfully."}</Small>
            )}
            <div className="flex items-center justify-end ml-auto">
              {isRetryingLoading ? (
                <div className="flex gap-1 w-max items-center pt-2">
                  <Loader className="w-4 h-4" />
                  <Small className="text-interaction-primary">Generating...</Small>
                </div>
              ) : (
                <Button
                  text="Retry"
                  appearance="text"
                  className="px-0! h-auto!"
                  onClick={handleRetryExport}
                  disabled={isRetryingLoading}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};
